/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, October 4th 2021, 11:18:57 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import TablePagination from "@mui/material/TablePagination";
import Paper from "@mui/material/Paper";
import {
  ObjectKey,
  // AllowedModelFieldSearchKey,
  DataSourceType,
  PaginationLimitOptions,
  APISearchParameters,
} from "lib/types";
import Box from "@mui/material/Box";
import { HeadCell } from "./DataTableHead";
import { APIListProgressWrapper } from "ui-widgets/wrappers/APIRequestWrappers";
import { DataTableToolbar } from "./DataTableToolbar";
import { useBuildDataTable } from "./useBuildDataTable";
import { TableList } from "./TableList";
import { GridList } from "./GridList";
import { EmptySearchResultsMessage } from "./EmptySearchResultsMessage";

type ColumnRenderProp<OSObjectType> = (rowItem: OSObjectType) => React.ReactNode;

export interface DataTableProps<OSObjectType, CustomKeys extends string = null> {
  dataSource: DataSourceType<OSObjectType>;
  title: string;
  headCells?: readonly HeadCell<OSObjectType, CustomKeys>[];
  dense?: boolean;
  initialRowsPerPage?: PaginationLimitOptions;
  defaultOrderBy: ObjectKey<OSObjectType>;
  titleKey: ObjectKey<OSObjectType>;
  uniqueKey: ObjectKey<OSObjectType>;
  searchKeys: ObjectKey<OSObjectType>[];
  errorMessage?: React.ReactNode;
  noDataMessage?: React.ReactNode;
  allowRowSelection?: boolean;
  showCellBorders?: boolean;
  showTableHead?: boolean;
  renderItemAvatar?: (rowItem: OSObjectType) => React.ReactNode;
  renderGridItem?: (rowItem: OSObjectType) => React.ReactNode;
  unit?: string;
  enableRowItemActions?: boolean;
  renderRowActions?: (item: OSObjectType) => React.ReactNode;
  listViewMode?: "table" | "grid" | "custom";
  showToolbar?: boolean;
  cellRenderProps?: { [key in keyof OSObjectType | CustomKeys]?: ColumnRenderProp<OSObjectType> };
  apiFilterParams?: APISearchParameters<OSObjectType>;
  children?: (rows: OSObjectType[]) => React.ReactNode;
  extraTools?: React.ReactNode;
}

export const DataTable = <OSObjectType, CustomKeys extends string = null>(
  props: DataTableProps<OSObjectType, CustomKeys>
) => {
  //Set up the data table state and event handlers using this custom Hook
  let {
    title,
    headCells,
    dense,
    uniqueKey,
    allowRowSelection = true,
    showCellBorders = true,
    showTableHead = true,
    errorMessage,
    noDataMessage,
    renderItemAvatar = null,
    renderGridItem = null,
    enableRowItemActions = true,
    unit,
    renderRowActions,
    listViewMode = "table",
    showToolbar = true,
    cellRenderProps = {},
    children,
    extraTools,
  } = props;

  const {
    allLoadedData,
    rowsToDisplay,
    page,
    rowsPerPage,
    rowCount,
    selectedRowsKeys,
    showLoader,
    showError,
    httpResponseStatusCode,
    order,
    orderBy,
    handleRequestSort,
    handleChangePage,
    handleChangeRowsPerPage,
    extraSearchProps,
    isFiltered,
    searchText,
    handleSelectAllClick,
    handleSelectRow,
    allRowsSelected,
    rowIsSelected,
    handleRefreshList,
  } = useBuildDataTable<OSObjectType, CustomKeys>(props);

  //OTB Get keys to reference data
  const columnKeys = headCells?.map((v) => v.id) ?? [];

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rowCount) : 0;

  let listView: React.ReactNode;

  switch (listViewMode) {
    case "table":
      listView = (
        <TableList
          headCells={headCells}
          data={rowsToDisplay}
          selectedRowsKeys={selectedRowsKeys}
          rowCount={rowCount}
          dense={dense}
          uniqueKey={uniqueKey}
          allowRowSelection={allowRowSelection}
          showCellBorders={showCellBorders}
          showTableHead={showTableHead}
          renderItemAvatar={renderItemAvatar}
          renderRowActions={renderRowActions}
          enableRowItemActions={enableRowItemActions}
          columnKeys={columnKeys}
          emptyRows={emptyRows}
          order={order}
          orderBy={orderBy}
          handleRequestSort={handleRequestSort}
          handleSelectAllClick={handleSelectAllClick}
          handleSelectRow={handleSelectRow}
          allRowsSelected={allRowsSelected}
          rowIsSelected={rowIsSelected}
          cellRenderProps={cellRenderProps}
        />
      );
      break;

    case "grid":
      listView = <GridList data={rowsToDisplay} renderGridItem={renderGridItem} uniqueKey={uniqueKey} />;
      break;

    case "custom":
      listView = children?.(rowsToDisplay);
      break;

    default:
      listView = null;
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Paper className="os-datatable-paper" sx={{ width: "100%", mb: 2 }}>
        {showToolbar ? (
          <DataTableToolbar
            numSelected={selectedRowsKeys.length}
            title={title}
            unit={unit}
            extraSearchProps={extraSearchProps}
            handleRefreshList={handleRefreshList}
            extraTools={extraTools}
          />
        ) : null}

        <APIListProgressWrapper
          showLoader={showLoader}
          isAPIError={showError}
          errorMessage={errorMessage}
          noDataMessage={isFiltered ? <EmptySearchResultsMessage unit={unit} searchText={searchText} /> : noDataMessage}
          items={allLoadedData}
          httpResponseStatusCode={httpResponseStatusCode}
          loaderType="linear"
        >
          {/* Adding the wrapper above here so that content below can still be seen as we load other items */}

          {listView}

          {rowCount > 5 ? (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={rowCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          ) : null}
        </APIListProgressWrapper>
      </Paper>
    </Box>
  );
};
